import { useEffect } from "react";

/** Height of the sticky bar plus a little breathing room.
    Kept in step with `scroll-padding-top` in index.css. */
export const HEADER_OFFSET = 84;

/** requestAnimationFrame with a timer fallback (tests, ancient browsers). */
const nextFrame = (callback) =>
  typeof window.requestAnimationFrame === "function"
    ? window.requestAnimationFrame(callback)
    : window.setTimeout(callback, 16);

/**
 * Scroll an in-page id into view, clear of the sticky header.
 * `#top` means the top of the document.
 */
export const scrollToId = (id, behavior = "smooth") => {
  if (typeof window === "undefined") return;

  if (id === "top") {
    window.scrollTo({ top: 0, behavior });
    return;
  }

  const target = document.getElementById(id);
  if (!target) return;

  const scroller = document.scrollingElement || document.documentElement;
  const fromTop = window.scrollY ?? scroller.scrollTop ?? 0;
  const top = target.getBoundingClientRect().top + fromTop - HEADER_OFFSET;

  window.scrollTo({ top: Math.max(top, 0), behavior });
};

/**
 * Smooth scrolling for in-page anchors.
 *
 * `behavior: "smooth"` is passed explicitly. Note that "auto" is not an
 * instant scroll - per spec it falls back to whatever `scroll-behavior` is
 * set to, and browsers may drop the animation entirely when the operating
 * system has Reduce Motion turned on. Explicit "smooth" is the only value
 * that reliably animates.
 */
const useSmoothScroll = () => {
  useEffect(() => {
    const handleClick = (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const source = event.target instanceof Element ? event.target : null;
      const link = source?.closest('a[href^="#"]');
      if (!link) return;

      const id = decodeURIComponent(link.getAttribute("href").slice(1));
      if (!id) return;
      if (id !== "top" && !document.getElementById(id)) return;

      event.preventDefault();

      try {
        window.history.replaceState(null, "", `#${id}`);
      } catch (error) {
        // Sandboxed or file:// contexts refuse this; scrolling still works.
      }

      // Measure on the next frame: closing the mobile drawer also happens on
      // this click, and positions should be read once the layout has settled.
      nextFrame(() => {
        nextFrame(() => scrollToId(id));
      });
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);
};

export default useSmoothScroll;
